import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ApprovalRow {
  readonly approvalId: string;
  readonly approvalCode: string;
  readonly approvalName: string;
  readonly approvalType: string;
  readonly status: string;
}

const APPROVAL_TYPES = ['', 'SEQUENTIAL', 'PARALLEL', 'CONDITIONAL', 'HIERARCHICAL', 'ROLE_BASED', 'VALUE_BASED', 'SLA_BASED'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = APPROVAL_TYPES.filter(Boolean);

/**
 * M28 — Approval Designer (§12 / §32). Configures multi-step approval chains
 * across the seven approval strategies (sequential, parallel, conditional,
 * hierarchical, role-based, value-based, SLA-based). Lists every
 * approval_definition (T370) with its strategy and lifecycle status, filterable
 * by approval type and status. Supports create → publish, wired to the guarded
 * /v1/lcnc/approvals write API. Publish requires at least one configured step.
 */
export function M28ApprovalDesignerPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [approvalType, setApprovalType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('SEQUENTIAL');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (approvalType) params.set('approvalType', approvalType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ApprovalRow[] }>(`/v1/lcnc/approvals${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, approvalType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.approvalDesigner.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.approvalDesigner.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.approvalDesigner.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ApprovalRow }>(`/v1/lcnc/approvals`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), approvalName: name.trim(), approvalType: newType },
        token,
      });
      setNotice(tr('m28.approvalDesigner.noticeCreated', { code: r.data.approvalCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.approvalDesigner.verbCreate'), 'APPROVAL_DESIGN'));
    } finally {
      setBusy(false);
    }
  };

  const publish = async (a: ApprovalRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/lcnc/approvals/${a.approvalId}/publish`, { method: 'POST', body: {}, token });
      setNotice(tr('m28.approvalDesigner.noticePublished', { code: a.approvalCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.approvalDesigner.verbPublish'), 'APPROVAL_DESIGN'));
    }
  };

  return (
    <main aria-label="approval-designer">
      <h1>{tr('m28.approvalDesigner.title')}</h1>

      <section aria-label="create-approval">
        <h2>{tr('m28.approvalDesigner.newHeading')}</h2>
        <form aria-label="approval-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.approvalDesigner.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.approvalDesigner.fieldName')}<input aria-label="field-approval-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.approvalDesigner.fieldType')}
            <select aria-label="field-approval-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-approval" disabled={busy}>{busy ? tr('m28.approvalDesigner.creating') : tr('m28.approvalDesigner.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="approval-error">{actionError}</p>}
        {notice && <p aria-label="approval-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.approvalDesigner.filterType')}
          <select aria-label="filter-approval-type" value={approvalType} onChange={(e) => setApprovalType(e.target.value)}>
            {APPROVAL_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.approvalDesigner.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.approvalDesigner.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.approvalDesigner.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.approvalDesigner.loading')}</p> : (
        <>
          <p aria-label="approval-count">{tr('m28.approvalDesigner.count', { count: rows.length })}</p>
          <table aria-label="approval-table">
            <thead><tr><th>{tr('m28.approvalDesigner.colCode')}</th><th>{tr('m28.approvalDesigner.colName')}</th><th>{tr('m28.approvalDesigner.colType')}</th><th>{tr('m28.approvalDesigner.colStatus')}</th><th>{tr('m28.approvalDesigner.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.approvalId}>
                  <td>{a.approvalCode}</td><td>{a.approvalName}</td><td>{a.approvalType}</td><td>{a.status}</td>
                  <td><button type="button" aria-label={`publish-${a.approvalCode}`} onClick={() => void publish(a)}>{tr('m28.approvalDesigner.btnPublish')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
